/**
 * Navigation Component
 * 
 * Implements role-based menu items (admin, coach, user)
 * Highlights active route
 * Mobile-friendly hamburger menu
 * 
 * Requirements: 1.9, 1.10, 1.11, 10.1, 10.2
 */

import { useState } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';

interface NavigationProps {
  className?: string;
}

interface NavItem {
  label: string;
  path: string;
  roles?: string[]; // If undefined, available to all authenticated users
}

export function Navigation({ className = '' }: NavigationProps) {
  const { isAuthenticated, isAdmin, isCoach } = useAuth();
  const hasRiders = useAuthStore(state => state.hasRiders);
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Get current path for active route highlighting
  const currentPath = location.pathname;
  const isOnDashboard = currentPath === '/dashboard';

  // Non-admin users with no riders should only see Dashboard (and Logout in header)
  const noRiderGuardActive = !isAdmin && hasRiders === false;

  /**
   * Define navigation items based on roles
   * Requirements: 1.9, 1.10, 1.11
   */
  const navItems: NavItem[] = [
    // All authenticated users
    { label: 'Dashboard', path: '/dashboard' },
    { label: 'MOTD', path: '/admin/motd', roles: ['admin'] },
    { label: 'RSVP', path: '/rsvp' },
    { label: 'Events', path: '/events' },
    { label: 'Calendar', path: '/calendar' },
    
    // Coach and Admin
    { label: 'Check-in', path: '/checkin', roles: ['coach', 'admin'] },
    
    // All authenticated users
    { label: 'Leaderboard', path: '/leaderboard' },
    
    // Coach and Admin
    { label: 'Results', path: '/results', roles: ['coach', 'admin'] },
    { label: 'Training', path: '/skills', roles: ['coach', 'admin'] },
    
    // Admin only
    { label: 'Riders', path: '/riders', roles: ['admin'] },
    { label: 'Users', path: '/admin/users', roles: ['admin'] },
    
    // All authenticated users
    { label: 'Profile', path: '/profile' },
    
    // Admin only
    { label: 'Reports', path: '/admin/reports', roles: ['admin'] },
    { label: 'Configuration', path: '/admin/configuration', roles: ['admin'] },
  ];

  /**
   * Filter navigation items based on user role
   */
  const getVisibleNavItems = (): NavItem[] => {
    if (!isAuthenticated) {
      return [];
    }

    // Non-admin users with no riders only see Dashboard
    if (noRiderGuardActive) {
      return navItems.filter(item => item.path === '/dashboard');
    }

    return navItems.filter((item) => {
      // If no roles specified, show to all authenticated users
      if (!item.roles || item.roles.length === 0) {
        return true;
      }

      // Check if user has required role
      if (isAdmin && item.roles.includes('admin')) {
        return true;
      }

      if (isCoach && item.roles.includes('coach')) {
        return true;
      }

      return false;
    });
  };

  /**
   * Check if a nav item is active
   */
  const isActive = (path: string): boolean => {
    return currentPath === path || currentPath.startsWith(path + '/');
  };

  /**
   * Handle navigation click
   */
  const handleNavClick = (path: string) => {
    // Close mobile menu
    setIsMobileMenuOpen(false);
    
    // Navigate using React Router
    navigate(path);
  };

  /**
   * Toggle mobile menu
   */
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  if (!isAuthenticated) {
    return null;
  }

  const visibleItems = getVisibleNavItems();

  return (
    <nav
      className={`bg-white ${className}`}
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="container mx-auto px-4">
        {/* Desktop Navigation - Requirements: 10.1 */}
        <div className="hidden md:flex space-x-1 py-2">
          {visibleItems.map((item) => (
            <button
              key={item.path}
              onClick={() => handleNavClick(item.path)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                isActive(item.path)
                  ? 'bg-primary-500 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
              aria-current={isActive(item.path) ? 'page' : undefined}
            >
              {item.label}
            </button>
          ))}
        </div>

        {/* Mobile Navigation - Requirements: 10.2 */}
        <div className="md:hidden">
          {/* Mobile Menu Button */}
          <div className="flex items-center justify-between py-3">
            {/* Dashboard Button - Only show when not on dashboard - Left aligned */}
            {!isOnDashboard && (
              <button
                onClick={() => handleNavClick('/dashboard')}
                className="p-2 rounded-md text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                aria-label="Go to Dashboard"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  />
                </svg>
              </button>
            )}
            
            {/* Menu label and hamburger button - Right aligned */}
            <div className="flex items-center space-x-2 ml-auto">
              <span className="text-sm font-medium text-gray-700">
                Menu
              </span>
              <button
                onClick={toggleMobileMenu}
                className="p-2 rounded-md text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                aria-expanded={isMobileMenuOpen}
                aria-label="Toggle navigation menu"
              >
                {isMobileMenuOpen ? (
                  // Close icon
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                ) : (
                  // Hamburger icon
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Mobile Menu Items */}
          {isMobileMenuOpen && (
            <div className="pb-3 space-y-1">
              {visibleItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => handleNavClick(item.path)}
                  className={`w-full text-left px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive(item.path)
                      ? 'bg-primary-500 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                  aria-current={isActive(item.path) ? 'page' : undefined}
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}

export default Navigation;
